import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { VideosLayout } from "@/components/videos/VideosLayout";
import { VideoCard, VideoGrid } from "@/components/videos/VideoCard";
import { useVideosData } from "@/hooks/useVideosData";

export const VideosAllPage: React.FC<{ shortsOnly?: boolean }> = ({
  shortsOnly = false,
}) => {
  const { videos, collections, loading, error, refetch } = useVideosData();
  const [activeFilter, setActiveFilter] = useState<string>("All");

  const displayVideos = shortsOnly ? videos.filter((v) => v.short) : videos;

  const filteredVideos = displayVideos.filter((v) => {
    if (activeFilter === "All") return true;
    if (activeFilter === "Tips") return v.category?.toLowerCase() === "tips";
    if (activeFilter === "Before & After")
      return (
        v.category?.toLowerCase() === "before-after" ||
        v.title.toLowerCase().includes("before")
      );
    if (activeFilter === "Setups")
      return (
        v.category?.toLowerCase() === "setups" ||
        v.title.toLowerCase().includes("setup")
      );
    return true;
  });

  const pageTitle = shortsOnly
    ? "Shorts | JNN Homes Northern Virginia"
    : "All Videos | JNN Homes Northern Virginia";
  const canonicalUrl = shortsOnly
    ? "https://www.jnnhomes.com/videos/shorts"
    : "https://www.jnnhomes.com/videos/all";

  return (
    <VideosLayout loading={loading} error={error} onRetry={refetch}>
      <SEOHead
        title={pageTitle}
        description="Browse all vacation rental management videos, tips, and property walkthroughs from JNN Homes."
        canonical={canonicalUrl}
      />

      <div className="space-y-6">
        <nav className="flex items-center gap-2 text-xs text-slate-500 font-medium">
          <Link to="/videos" className="hover:text-[#9A5207]">
            Videos
          </Link>
          <span>›</span>
          <span className="text-slate-900 font-semibold">
            {shortsOnly ? "Shorts" : "All Videos"}
          </span>
        </nav>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 pb-4">
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900">
              {shortsOnly ? "Shorts" : "All Videos"}
            </h1>
            <p className="text-xs text-slate-500 mt-1">
              Showing {filteredVideos.length} videos
            </p>
          </div>

          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
            {["All", "Tips", "Before & After", "Setups"].map((chip) => (
              <button
                key={chip}
                onClick={() => setActiveFilter(chip)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-[#D97706] ${
                  activeFilter === chip
                    ? "bg-[#B35A06] text-white"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                {chip}
              </button>
            ))}
          </div>
        </div>

        <VideoGrid>
          {filteredVideos.map((video) => {
            const parentCol = collections.find((c) =>
              c.lessons.includes(video.id),
            );
            return (
              <VideoCard
                key={video.id}
                video={video}
                collectionSlug={parentCol?.slug}
              />
            );
          })}
        </VideoGrid>
      </div>
    </VideosLayout>
  );
};

export const VideosSearchPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const { videos, categories, collections, loading, error, refetch } =
    useVideosData();

  const queryLower = query.toLowerCase().trim();

  const matchingVideos = videos.filter(
    (v) =>
      v.title.toLowerCase().includes(queryLower) ||
      v.category?.toLowerCase().includes(queryLower),
  );

  const matchingCollections = collections.filter(
    (c) =>
      c.title.toLowerCase().includes(queryLower) ||
      c.description.toLowerCase().includes(queryLower) ||
      c.category.toLowerCase().includes(queryLower),
  );

  const matchingCategories = categories.filter(
    (cat) =>
      cat.name.toLowerCase().includes(queryLower) ||
      cat.description.toLowerCase().includes(queryLower),
  );

  return (
    <VideosLayout
      loading={loading}
      error={error}
      onRetry={refetch}
      searchValue={query}
    >
      <SEOHead
        title={`Search results for "${query}" | JNN Homes Videos`}
        description={`Search results for "${query}" on JNN Homes vacation rental management videos.`}
        noIndex={true}
      />

      <div className="space-y-8">
        <nav className="flex items-center gap-2 text-xs text-slate-500 font-medium">
          <Link to="/videos" className="hover:text-[#9A5207]">
            Videos
          </Link>
          <span>›</span>
          <span className="text-slate-900 font-semibold">Search</span>
        </nav>

        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">
            Search results for "{query}"
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Found {matchingVideos.length} videos, {matchingCollections.length}{" "}
            collections, {matchingCategories.length} categories
          </p>
        </div>

        {matchingCategories.length > 0 && (
          <section className="space-y-3">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500">
              Matching Categories
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {matchingCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/videos/c/${cat.slug}`}
                  className="p-3 rounded-xl border border-slate-200 hover:border-[#E8850F] transition-colors flex justify-between items-center"
                >
                  <span className="font-bold text-sm text-slate-900">
                    {cat.name}
                  </span>
                  <span className="text-xs text-slate-500">
                    {cat.count} videos
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {matchingCollections.length > 0 && (
          <section className="space-y-3">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500">
              Matching Collections
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {matchingCollections.map((col) => (
                <Link
                  key={col.slug}
                  to={`/videos/course/${col.slug}`}
                  className="p-4 rounded-xl border border-slate-200 hover:border-[#E8850F] transition-colors space-y-2"
                >
                  <h3 className="font-bold text-base text-slate-900">
                    {col.title}
                  </h3>
                  <p className="text-xs text-slate-600 line-clamp-2">
                    {col.description}
                  </p>
                </Link>
              ))}
            </div>
          </section>
        )}

        <section className="space-y-4">
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500">
            Matching Videos ({matchingVideos.length})
          </h2>
          {matchingVideos.length === 0 ? (
            <p className="text-sm text-slate-500 py-8 text-center bg-slate-50 rounded-xl">
              No videos matched your search query. Try searching for "tips",
              "setup", or "decor".
            </p>
          ) : (
            <VideoGrid>
              {matchingVideos.map((video) => {
                const parentCol = collections.find((c) =>
                  c.lessons.includes(video.id),
                );
                return (
                  <VideoCard
                    key={video.id}
                    video={video}
                    collectionSlug={parentCol?.slug}
                  />
                );
              })}
            </VideoGrid>
          )}
        </section>
      </div>
    </VideosLayout>
  );
};
